import type { MouseEvent, ReactNode } from 'react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Button, css, useTheme } from '@mui/material';
import { Tooltip } from '@components/tooltip/Tooltip';
import { fonts } from '@theme/fontsCustomer';
import { CircleFillIcon } from '@icons/CircleFillIcon';
import { transientOptions } from '@utils/CommonUtil';
import { numberFormat } from '@utils/numberFormat';

type TrendColorTokens = {
  positive: string;
  negative: string;
  neutral: string;
};

const getTrendColor = (value: number, colors: TrendColorTokens): string => {
  if (value > 0) return colors.positive;
  if (value < 0) return colors.negative;
  return colors.neutral;
};

/**
 * SaaSOps 지표 기반 5개 카드에 공용으로 사용되는 범용 카드.
 */

export type SegmentedBadge = {
  label: string;
  value: number;
  onClick?: () => void;
  active?: boolean;
};

export type InlineItem = {
  icon?: ReactNode;
  label: string;
  count: number;
  onClick?: () => void;
  active?: boolean;
  tooltip?: ReactNode;
};

type Props = {
  id: string;
  title: string;
  value: string;
  subLabel?: string;
  trendValue?: number;
  badgeColor?: string;
  segmentedBadges?: SegmentedBadge[];
  inlineItems?: InlineItem[];
  isDanger?: boolean;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  className?: string;
};

export const SaasOverviewCard = ({
  id,
  title,
  value,
  subLabel,
  trendValue,
  badgeColor,
  segmentedBadges,
  inlineItems,
  isDanger,
  onClick,
  className,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const hasTrend = typeof trendValue === 'number' && trendValue !== 0;
  const safeTrendValue = trendValue ?? 0;
  const trendIsUp = safeTrendValue > 0;

  return (
    <CardButton
      className={className}
      $clickable={Boolean(onClick)}
      onClick={onClick}
      data-card-id={id}
    >
      <Header>
        <Title $badgeColor={badgeColor} $isDanger={Boolean(isDanger)}>
          {title}
        </Title>
      </Header>
      <Contents>
        <Value className="count" $isDanger={Boolean(isDanger)}>
          {value}
        </Value>
        <MetaWrapper>
          {segmentedBadges && segmentedBadges.length > 0 ? (
            <SegmentedList>
              {segmentedBadges.map(badge => (
                <SegmentedItem
                  key={badge.label}
                  type="button"
                  $clickable={Boolean(badge.onClick)}
                  $active={Boolean(badge.active)}
                  disabled={!badge.onClick}
                  onClick={event => {
                    if (!badge.onClick) return;
                    event.stopPropagation();
                    badge.onClick();
                  }}
                >
                  <SegmentedLabel $active={Boolean(badge.active)}>
                    {badge.label}({badge.value})
                  </SegmentedLabel>
                </SegmentedItem>
              ))}
            </SegmentedList>
          ) : inlineItems && inlineItems.length > 0 ? (
            <InlineItemList>
              {inlineItems.map(item => {
                const chip = (
                  <InlineItemChip
                    type="button"
                    $clickable={Boolean(item.onClick)}
                    $active={Boolean(item.active)}
                    disabled={!item.onClick}
                    onClick={event => {
                      if (!item.onClick) return;
                      event.stopPropagation();
                      item.onClick();
                    }}
                  >
                    {item.icon ? <InlineItemIcon aria-hidden>{item.icon}</InlineItemIcon> : null}
                    <InlineItemLabel>{item.label}</InlineItemLabel>
                    <InlineItemCount>{item.count}</InlineItemCount>
                  </InlineItemChip>
                );
                if (item.tooltip) {
                  return (
                    <Tooltip key={item.label} label={item.tooltip} placement="top-center">
                      {chip}
                    </Tooltip>
                  );
                }
                return <Fragment key={item.label}>{chip}</Fragment>;
              })}
            </InlineItemList>
          ) : (
            <TrendGroup>
              {subLabel ? <Comparison>{subLabel}</Comparison> : null}
              {hasTrend ? (
                <>
                  <CircleFillIcon
                    width={16}
                    height={16}
                    color={trendIsUp ? theme.colors['ic-green-light'] : theme.colors['ic-red-light']}
                    rotateNum={trendIsUp ? 0 : 180}
                  />
                  <TrendValue $value={safeTrendValue}>
                    {numberFormat({ num: Math.abs(safeTrendValue * 100), maxFractionDigit: 1 })}
                    {t('Subscrib_Main_Overview_PercentSuffix')}
                  </TrendValue>
                </>
              ) : null}
            </TrendGroup>
          )}
        </MetaWrapper>
      </Contents>
    </CardButton>
  );
};

const CardButton = styled(Button, transientOptions)<{ $clickable: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: space-between;
  width: 100%;
  min-width: 260px;
  height: 140px;
  text-align: left;
  padding: 34px 36px 24px 36px;
  flex-grow: 1;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'auto')};
  border-radius: 0;

  &:after {
    content: '';
    width: 100%;
    height: 140px;
    position: absolute;
    top: 0;
    left: 0;
    border: 1px solid ${({ theme }) => theme.colors['border-gray-dark']};
    border-right-color: ${({ theme }) => theme.colors['border-gray-lighter']};
    border-width: 1px 1px 1px 0;
  }

  &:hover:after,
  &.clicked:after {
    ${({ $clickable, theme: { colors } }) =>
      $clickable
        ? css`
            border: 1px solid transparent;
            background-image: linear-gradient(
                ${colors['state-white-primary-hover']},
                ${colors['state-white-primary-hover']}
              ),
              ${colors['border-gradient']};
            background-origin: border-box;
            background-clip: content-box, border-box;
          `
        : ''}
  }

  &.clicked:after {
    background-image: linear-gradient(#fff, #fff), ${({ theme }) => theme.colors['border-gradient']};
    border-width: 2px;
  }
  &.clicked {
    & .count {
      background: ${({ theme }) => theme.colors['text-gradient']};
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }

  &:first-of-type {
    &:after {
      border-width: 1px;
      border-radius: 5px 0 0 5px;
    }
    &.clicked:after {
      border-width: 2px;
    }
  }
  &:last-of-type {
    &:after {
      border-radius: 0 5px 5px 0;
      border-right-color: ${({ theme }) => theme.colors['border-gray-dark']};
    }
    &.clicked:after {
      border-radius: 0 5px 5px 0;
      border-right-color: transparent;
    }
  }
  &.prevButton:after {
    border-right-width: 0;
  }
  &.clicked + button:after {
    border-left-width: 0;
  }
  & .MuiTouchRipple-root {
    display: none;
  }
`;

const Header = styled.div`
  display: flex;
  z-index: 100;
`;

const Title = styled('span', transientOptions)<{ $badgeColor?: string; $isDanger: boolean }>`
  position: relative;
  ${fonts.Headline7};
  ${({ $badgeColor, $isDanger, theme: { colors } }) => {
    if ($isDanger) {
      return css`
        color: ${colors['text-red-dark']};
      `;
    }
    if ($badgeColor) {
      return css`
        padding-left: 14px;
        &::after {
          content: '';
          display: block;
          width: 4px;
          height: 17px;
          position: absolute;
          top: 50%;
          left: 0;
          transform: translate(0, -50%);
          background-color: ${$badgeColor};
          border-radius: 1px;
        }
      `;
    }
    return ``;
  }}
`;

const Contents = styled.div`
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: baseline;
  justify-content: flex-start;
  gap: 20px;
  z-index: 100;
`;

const Value = styled('span', transientOptions)<{ $isDanger: boolean }>`
  ${fonts.Headline11}
  white-space: nowrap;
  flex-shrink: 0;
  line-height: 1;
  ${({ $isDanger, theme }) =>
    $isDanger
      ? css`
          color: ${theme.colors['text-red']};
        `
      : ''}
`;

const MetaWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  flex: 1 1 auto;
  min-width: 0;
`;

const TrendGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const Comparison = styled.span`
  ${fonts.Body4};
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
  margin-right: 2px;
`;

const TrendValue = styled.span<{ $value: number }>`
  ${fonts.Headline7}
  margin-left: 2px;
  color: ${({ $value, theme: { colors } }) =>
    getTrendColor($value, {
      positive: colors['text-green-light'],
      negative: colors['text-red-light'],
      neutral: colors['text-gray-main'],
    })};
`;

const SegmentedList = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
`;

const SegmentedItem = styled('button', transientOptions)<{ $clickable: boolean; $active: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 2px 0;
  border: none;
  background: transparent;
  border-bottom: 2px solid
    ${({ $active, theme: { colors } }) => ($active ? colors['border-primary'] : 'transparent')};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: border-color 0.15s ease;

  &:hover {
    ${({ $clickable, $active, theme: { colors } }) =>
      $clickable && !$active
        ? css`
            border-bottom-color: ${colors['border-gray-light']};
          `
        : ''}
  }

  &:disabled {
    cursor: default;
  }
`;

const SegmentedLabel = styled('span', transientOptions)<{ $active: boolean }>`
  ${fonts.Body2};
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  color: ${({ $active, theme: { colors } }) =>
    $active ? colors['text-primary-light'] : colors['text-gray-sub-dark']};
  line-height: 1;
`;

const InlineItemList = styled.div`
  display: flex;
  gap: 6px;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
`;

const InlineItemChip = styled('button', transientOptions)<{ $clickable: boolean; $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  max-width: 100%;
  min-width: 0;
  border: 1px solid ${({ theme }) => theme.colors['border-gray-lighter']};
  border-radius: 999px;
  background: ${({ theme }) => theme.colors['bg-white']};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: border-color 0.15s ease, background-color 0.15s ease;

  ${({ $active, theme: { colors } }) =>
    $active
      ? css`
          border-color: ${colors['border-primary']};
          background: ${colors['state-white-primary-hover']};
        `
      : ''}

  &:hover {
    ${({ $clickable, theme: { colors } }) =>
      $clickable
        ? css`
            border-color: ${colors['border-primary']};
          `
        : ''}
  }

  &:disabled {
    cursor: default;
  }
`;

const InlineItemIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;

const InlineItemLabel = styled.span`
  ${fonts.Body4};
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const InlineItemCount = styled.span`
  ${fonts.Body2};
  font-weight: 600;
  color: ${({ theme }) => theme.colors['text-gray-main']};
`;
